import { Boundaries } from "@planzone/core";
import { events } from "@planzone/events";
import { createStateReducer } from "../../utils/createStateReducer";
import { setRequestStateToPending } from "../../utils/setRequestStateToPending";
import { setRequestStateToSuccess } from "../../utils/setRequestStateToSuccess";
import { setRequestStateToFailure } from "../../utils/setRequestStateToFailure";
import { State, Selector } from "./types";
import { createSelector } from "./selector";

const initialState: State = {
  request: {
    isPending: false,
    isSuccessful: false,
    isFailure: false,
  },
  identityProviders: [],
};

function handleRequest(state: State): State {
  return { ...state, request: setRequestStateToPending() };
}

function handleSuccess(
  state: State,
  { identityProviders }: Boundaries["retrieveIdentityProviders"]["success"],
): State {
  return {
    request: setRequestStateToSuccess(),
    identityProviders,
  };
}

function handleFailure(state: State): State {
  return { ...state, request: setRequestStateToFailure() };
}

function handleAddProvider(
  state: State,
  { identityProvider }: Boundaries["addIdentityProvider"]["success"],
): State {
  return {
    ...state,
    identityProviders: [identityProvider, ...state.identityProviders],
  };
}

function handleRemoveProvider(
  state: State,
  { identityProvider }: Boundaries["removeIdentityProvider"]["success"],
): State {
  return {
    ...state,
    identityProviders: state.identityProviders.filter(
      ({ uuid }) => uuid !== identityProvider.uuid,
    ),
  };
}

function handleEditProvider(
  state: State,
  { identityProvider }: Boundaries["editIdentityProvider"]["success"],
): State {
  const index = state.identityProviders.findIndex(
    ({ uuid }) => uuid === identityProvider.uuid,
  );

  return {
    ...state,
    identityProviders:
      index !== -1
        ? [
            ...state.identityProviders.slice(0, index),
            identityProvider,
            ...state.identityProviders.slice(index + 1),
          ]
        : state.identityProviders,
  };
}

const reduceState = createStateReducer(initialState, {
  [events.retrieveIdentityProvidersRequested]: handleRequest,
  [events.retrieveIdentityProvidersSucceeded]: handleSuccess,
  [events.retrieveIdentityProvidersFailed]: handleFailure,
  [events.addIdentityProviderSucceeded]: handleAddProvider,
  [events.removeIdentityProviderSucceeded]: handleRemoveProvider,
  [events.editIdentityProviderSucceeded]: handleEditProvider,
});

export type { State, Selector };

export { reduceState, initialState, createSelector };
